import { styled } from "@mui/material/styles";
import { Box, Button, Typography } from "@mui/material";
import HeroBackgroundImg from "../../assets/homePage/HeroBackground.jpg";
import colors from "../../theme/Colours";

export const HeroRoot = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "560px",
  width: "100%",
  overflow: "hidden",
  paddingTop: theme.spacing(8),
  color: "#fff",
}));

export const HeroBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  backgroundImage: `
    linear-gradient(90deg, rgba(5, 18, 30, 0.94) 0%, rgba(5, 18, 30, 0.82) 45%, rgba(5, 18, 30, 0.72) 100%),
    url(${HeroBackgroundImg})
  `,
  backgroundSize: "cover",
  backgroundPosition: "center",
  filter: "brightness(0.72)",
  zIndex: 0,
});

export const HeroContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  minHeight: "560px",
  display: "grid",
  gridTemplateColumns: "1.2fr 0.8fr",
  alignItems: "center",
  gap: theme.spacing(8),
  maxWidth: "1060px",
  margin: "0 auto",
  padding: theme.spacing(7, 3),

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(4),
  },
}));

export const LeftSection = styled(Box)({
  maxWidth: "560px",
});

export const SellerPill = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "7px 14px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "rgba(255,255,255,0.9)",
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "2px",
  textTransform: "uppercase",
});

export const HeroTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  maxWidth: "520px",
  fontSize: "50px",
  lineHeight: 1.08,
  fontWeight: 500,
  color: "#fff",

  [theme.breakpoints.down("sm")]: {
    fontSize: "36px",
  },
}));

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  maxWidth: "520px",
  fontSize: "16px",
  lineHeight: 1.6,
  fontWeight: 500,
  color: "rgba(255,255,255,0.92)",
}));

export const ButtonRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  maxWidth: "460px",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(4),
  flexWrap: "nowrap",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

export const PrimaryButton = styled(Button)({
  height: "44px",
  padding: "0 20px",
  borderRadius: "8px",
  background: colors.primaryButtonOrange,
  color: "#fff",
  textTransform: "none",
  fontWeight: 700,
  gap: "8px",

  "&:hover": {
    background: colors.primaryButtonOrangeHover,
  },
});

export const SecondaryButton = styled(Button)({
  height: "44px",
  padding: "0 10px",
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.35)",
  color: "#fff",
  background: "rgba(255,255,255,0.04)",
  textTransform: "none",
  fontWeight: 700,

  "&:hover": {
    background: "rgba(255,255,255,0.12)",
  },
});

export const StatsRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, max-content)",
  gap: "10px 24px",
  marginTop: theme.spacing(4),
}));

export const StatItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  color: "#fff",
  whiteSpace: "nowrap",

  "& svg": {
    color: "#ff6a00",
  },
});

export const DropsCard = styled(Box)(({ theme }) => ({
  width: "360px",
  justifySelf: "end",
  padding: theme.spacing(3),
  borderRadius: "24px",
  background:
    "linear-gradient(180deg, rgba(255, 120, 65, 0.2) 0%, rgba(12, 28, 40, 0.78) 55%)",
  border: "1px solid rgba(255,255,255,0.16)",
  boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
  backdropFilter: "blur(16px)",
  overflow: "hidden",

  [theme.breakpoints.down("md")]: {
    justifySelf: "start",
    width: "100%",
    maxWidth: "420px",
  },
}));

export const DropsHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "18px",
});

export const DropsTitle = styled(Typography)({
  color: colors.primaryButtonOrange,
  fontSize: "11px",
  letterSpacing: "3px",
  fontWeight: 800,
});

export const PrebookBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  background: "#ff6a00",
  color: "#111",
  fontSize: "10px",
  fontWeight: 500,
  padding: "4px 10px",
  borderRadius: "999px",
  lineHeight: 1,
});

export const DropsListWrapper = styled(Box)({
  height: "298px",
  overflow: "hidden",
  position: "relative",
});

export const VerticalCarousel = styled(Box)<{ $isAnimating: boolean }>(
  ({ $isAnimating }) => ({
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    transform: $isAnimating ? "translateY(-94px)" : "translateY(0)",
    transition: $isAnimating ? "transform 0.5s ease-in-out" : "none",
  })
);

export const ProductDropCard = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minHeight: "80px",
  height: "80px",
  padding: "12px",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.07)",
  border: "1px solid rgba(255,255,255,0.13)",
  boxSizing: "border-box",
  flexShrink: 0,
});

export const ProductImage = styled("img")({
  width: "56px",
  height: "56px",
  borderRadius: "12px",
  objectFit: "cover",
  flexShrink: 0,
});

export const ProductInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
});

export const ProductName = styled(Typography)({
  color: "#fff",
  fontSize: "13px",
  fontWeight: 800,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const ProductMeta = styled(Typography)({
  color: "rgba(255,255,255,0.68)",
  fontSize: "12px",
  marginTop: "4px",
  whiteSpace: "nowrap",
});

export const ArrowButton = styled(Button)({
  minWidth: "32px",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  color: "rgba(255,255,255,0.75)",

  "&:hover": {
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
  },
});
